import { useState, type ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import { industries, erp } from '../content';
import { usePageMeta, Swipe, Arrow, SmartLink, StatusChip, cx } from '../ui';
import { TagExplainer, HandoverExplainer, OwnerScanExplainer, TransferExplainer, ErpSyncExplainer, ClaimExplainer, type ExplainerProps } from '../explainers';
import { BookingWidget } from '../booking/BookingWidget';
import { sectorPages, sectorUnits, type ExplainerKey } from './industries/data';
import { UnitPhone } from './industries/UnitPhone';
import { ShareButtons } from './industries/Share';
import NotFound from './NotFound';
import { SectorHero, SECTOR_HEROES } from './industries/Hero';
import { FallbackImg } from './industries/Vignettes';

/** Editorial band; disappears (frame and all) if the photo and its fallback are both missing. */
function Band({ photo }: { photo: { src: string; alt: string; fallback?: string } }) {
  const [gone, setGone] = useState(false);
  if (gone) return null;
  return (
    <div className="frame mb-14 sm:mb-20">
      <FallbackImg
        src={photo.src}
        fallback={photo.fallback}
        alt={photo.alt}
        width={1536}
        height={1024}
        loading="lazy"
        className="aspect-[3/2] w-full object-cover sm:aspect-[21/8]"
        onGone={() => setGone(true)}
      />
    </div>
  );
}

const EXPLAINERS: Record<ExplainerKey, ComponentType<ExplainerProps>> = {
  tag: TagExplainer,
  handover: HandoverExplainer,
  owner: OwnerScanExplainer,
  transfer: TransferExplainer,
  erp: ErpSyncExplainer,
  claim: ClaimExplainer,
};

function Steps({ steps, unit }: { steps: (typeof sectorPages)[string]['steps']; unit?: ExplainerProps['unit'] }) {
  const [i, setI] = useState(0);
  const X = EXPLAINERS[steps[i].x];
  return (
    <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
      <div role="tablist" aria-label="How it works" className="border-t border-white/15">
        {steps.map((s, n) => (
          <button
            key={s.k}
            role="tab"
            aria-selected={i === n}
            onClick={() => setI(n)}
            className={cx('ind-step block w-full border-b border-white/15 py-6 text-left transition-colors', i === n ? 'is-on' : 'opacity-60 hover:opacity-90')}
          >
            <div className="flex items-baseline gap-4">
              <span className="text-[14px] font-semibold text-bm-lime">{n + 1}</span>
              <span className="text-[14px] font-medium text-white/60">{s.k}</span>
            </div>
            <div className="mt-2 pl-[30px] text-[21px] font-bold leading-snug tracking-[-0.02em] text-white">{s.t}</div>
            <div className={cx('grid pl-[30px] transition-[grid-template-rows] duration-300', i === n ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
              <p className="overflow-hidden pt-2 text-[16px] leading-relaxed text-white/70">{s.d}</p>
            </div>
          </button>
        ))}
      </div>
      <div className="lg:sticky lg:top-28 lg:self-start">
        <X key={steps[i].x} tone="dark" unit={unit} />
      </div>
    </div>
  );
}

export default function Industry() {
  const { slug = '' } = useParams();
  const ind = industries.find((x) => x.slug === slug);
  const page = sectorPages[slug];
  const hero = SECTOR_HEROES[slug];
  const idx = industries.findIndex((x) => x.slug === slug);
  usePageMeta(page ? page.seoTitle : 'Industry not found', page?.seoDesc);
  if (!ind || !page || !hero) return <NotFound hint="industry" />;

  const next = industries[(idx + 1) % industries.length];
  const subject = `Worth 30 minutes? BrandedMark for ${ind.name.toLowerCase()}`;
  const body = (url: string) =>
    `Hi,\n\nHave a look at this. It shows ${page.forward}.\n\nIn short: ${ind.sub}\n\nThe team behind it has built ERP for manufacturers, it connects to the ERP we already run, it is built for GDPR, and the prices are public. A pilot is one product line.\n\n${url}\n\nWorth a 30-minute call?\n`;

  return (
    <div className="ind-page">
      <SectorHero name={ind.name} sub={ind.sub} slug={ind.slug} page={page} spec={hero} />

      {/* Sound familiar */}
      <section className="border-t border-bm-line">
        <div className="wrap section grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <span className="bar" />
            <h2 className="t-h2">
              Where it goes <Swipe>wrong.</Swipe>
            </h2>
            <p className="t-lede mt-5 max-w-[34ch]">{page.painsIntro}</p>
          </div>
          <ol className="border-t border-bm-indigo">
            {page.pains.map((p, n) => (
              <li key={p.said} className="grid grid-cols-[40px_1fr] gap-3 border-b border-bm-line py-8 sm:grid-cols-[56px_1fr]">
                <span className="pt-1.5 text-[14px] font-semibold text-bm-emerald">{n + 1}</span>
                <div>
                  <h3 className="text-[22px] font-bold leading-snug tracking-[-0.02em] text-bm-indigo sm:text-[25px]">{p.said}</h3>
                  <p className="mt-2.5 max-w-[56ch] text-[16.5px] leading-relaxed text-bm-muted">{p.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* How it works: one dark band, one headline */}
      <section className="on-dark bg-bm-indigo">
        <div className="wrap py-20 sm:py-28">
          <h2 className="t-h2 mb-12 max-w-[20ch]">{ind.hero}</h2>
          <Steps steps={page.steps} unit={sectorUnits[slug]} />
        </div>
      </section>

      {/* Roles, beside what the owner actually sees */}
      <section>
        <div className="wrap section">
          {page.photos[1] && <Band photo={page.photos[1]} />}
          <div className="grid gap-14 lg:grid-cols-[0.75fr_1.25fr] lg:gap-20">
            <figure className="hidden lg:sticky lg:top-28 lg:flex lg:flex-col lg:items-center lg:self-start">
              <UnitPhone unit={page.unit} />
              <figcaption className="mt-5 max-w-[32ch] text-center text-[13.5px] text-bm-muted">
                One scan opens that unit, in the maker’s brand. {page.unitCaption}
              </figcaption>
            </figure>
            <div>
              <span className="bar" />
              <h2 className="t-h2 max-w-[16ch]">
                Who it <Swipe>helps.</Swipe>
              </h2>
              <dl className="mt-12 border-t border-bm-indigo">
                {page.roles.map((r) => (
                  <div key={r.who} className="grid gap-3 border-b border-bm-line py-7 sm:grid-cols-[150px_1fr] sm:gap-8">
                    <dt className="text-[19px] font-bold tracking-[-0.015em] text-bm-indigo">{r.who}</dt>
                    <dd>
                      <p className="text-[16px] text-bm-muted">
                        <span className="font-semibold">Today: </span>
                        {r.before}
                      </p>
                      <p className="mt-2 text-[17px] font-medium leading-snug text-bm-ink">
                        <span className="font-semibold text-bm-emerald">With BrandedMark: </span>
                        {r.after}
                      </p>
                    </dd>
                  </div>
                ))}
              </dl>
              {page.formExample && (
                <SmartLink href="/platform/forms" className="group mt-8 inline-flex flex-wrap items-baseline gap-x-2 text-[15.5px] text-bm-muted">
                  <span>Your forms, signed on site:</span>
                  <span className="font-semibold text-bm-indigo group-hover:text-bm-emerald">
                    {page.formExample.t}
                    <span className="hidden sm:inline">,</span>
                  </span>
                  <span className="text-bm-muted">{page.formExample.who.toLowerCase()}</span>
                  <Arrow />
                </SmartLink>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Compliance + ERP */}
      <section className="bg-bm-alt">
        <div className="wrap section grid gap-6 lg:grid-cols-[1.25fr_1fr]">
          <div className="rounded-[22px] bg-white p-7 ring-1 ring-bm-line sm:p-10">
            <h2 className="t-h3 text-[clamp(24px,2.4vw,30px)]">{page.compliance.t}</h2>
            <p className="mt-4 text-[16.5px] leading-relaxed text-bm-muted">{page.compliance.d}</p>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              <div>
                <div className="text-[14.5px] font-semibold text-bm-emerald">Decided</div>
                <ul className="mt-3 space-y-3 text-[15px] text-bm-ink">
                  {page.compliance.decided.map((d) => (
                    <li key={d} className="border-l-2 border-bm-green pl-3">{d}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[14.5px] font-semibold text-bm-muted">Not decided yet</div>
                <ul className="mt-3 space-y-3 text-[15px] text-bm-ink">
                  {page.compliance.open.map((d) => (
                    <li key={d} className="border-l-2 border-bm-line pl-3">{d}</li>
                  ))}
                </ul>
              </div>
            </div>
            <SmartLink href="/platform/digital-product-passport" className="mt-8 inline-flex items-center gap-2 text-[15px] font-semibold text-bm-indigo hover:text-bm-emerald">
              Passport rules by product group <Arrow />
            </SmartLink>
          </div>
          <div className="flex flex-col rounded-[22px] bg-white p-7 ring-1 ring-bm-line sm:p-10">
            <h2 className="t-h3 text-[clamp(24px,2.4vw,30px)]">Your ERP stays the master.</h2>
            <ul className="mt-6 divide-y divide-bm-line border-y border-bm-line">
              {erp.systems.map((s) => (
                <li key={s.name} className="flex items-center justify-between gap-4 py-3.5 text-[15.5px]">
                  <span className="font-medium text-bm-indigo">{s.name}</span>
                  <StatusChip status={s.status} />
                </li>
              ))}
            </ul>
            <p className="mt-5 text-[14px] text-bm-muted">Epicor Kinetic has a native connector. The rest connect through our API.</p>
            <SmartLink href="/integrations" className="mt-auto inline-flex items-center gap-2 pt-6 text-[15px] font-semibold text-bm-indigo hover:text-bm-emerald">
              How the ERP connection works <Arrow />
            </SmartLink>
          </div>
        </div>
      </section>

      {/* Forward + reading */}
      <section>
        <div className="wrap section grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <div className="frame">
            <div className="p-7 sm:p-10">
              <h2 className="t-h3 text-[clamp(24px,2.4vw,30px)]">The short version.</h2>
              <dl className="mt-7 divide-y divide-bm-line border-y border-bm-line text-[15.5px]">
                <div className="grid gap-1 py-4 sm:grid-cols-[140px_1fr] sm:gap-6">
                  <dt className="font-semibold text-bm-indigo">What it is</dt>
                  <dd className="text-bm-ink">A record for every unit you make. The {page.channel} registers it, owners scan it, you see every claim.</dd>
                </div>
                <div className="grid gap-1 py-4 sm:grid-cols-[140px_1fr] sm:gap-6">
                  <dt className="font-semibold text-bm-indigo">For us</dt>
                  <dd className="text-bm-ink">{ind.wins.join('. ')}.</dd>
                </div>
                <div className="grid gap-1 py-4 sm:grid-cols-[140px_1fr] sm:gap-6">
                  <dt className="font-semibold text-bm-indigo">To try it</dt>
                  <dd className="text-bm-ink">
                    One product line, and your data stays yours.{' '}
                    <SmartLink href="/why-trust-us" className="font-medium text-bm-emerald underline underline-offset-2">
                      Why trust us
                    </SmartLink>
                  </dd>
                </div>
              </dl>
              <ShareButtons className="mt-7" subject={subject} body={body} />
            </div>
          </div>
          <div>
            <div className="t-eyebrow">Further reading</div>
            <ul className="mt-5 border-t border-bm-line">
              {page.posts.map((p) => (
                <li key={p.slug} className="border-b border-bm-line">
                  <a href={`/blog/${p.slug}/`} className="group flex items-start justify-between gap-6 py-5">
                    <span className="text-[17.5px] font-semibold leading-snug tracking-[-0.01em] text-bm-indigo group-hover:text-bm-emerald">{p.title}</span>
                    <span className="mt-1 shrink-0 text-bm-muted transition-transform duration-200 group-hover:translate-x-1 group-hover:text-bm-emerald">
                      <Arrow />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <SmartLink href={`/industries/${next.slug}`} className="mt-10 block rounded-2xl bg-bm-alt p-6 hover:ring-1 hover:ring-bm-indigo">
              <span className="text-[12.5px] text-bm-muted">Next sector</span>
              <span className="mt-1 flex items-center justify-between gap-4 text-[18px] font-bold tracking-[-0.015em] text-bm-indigo">
                {next.name} <Arrow />
              </span>
              <span className="mt-1 block text-[14.5px] text-bm-muted">{next.short}</span>
            </SmartLink>
          </div>
        </div>
      </section>

      {/* Book */}
      <section className="on-dark bg-bm-deep">
        <div className="wrap py-20 sm:py-28">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end">
            <div>
              <h2 className="t-h2 max-w-[18ch]">
                Let&rsquo;s <Swipe>talk.</Swipe>
              </h2>
            </div>
            <p className="t-lede max-w-[46ch] lg:justify-self-end">
              Name one {ind.slug === 'industrial-precision' ? 'delivery' : 'product'}. Leave with an honest view of fit, effort and cost.
            </p>
          </div>
          <div className="mt-12 min-w-0">
            <BookingWidget compact source={`industry-${ind.slug}`} />
          </div>
        </div>
      </section>

      <style>{`
        .v2 .ind-step.is-on { opacity: 1; }
        .v2 .ind-step:focus-visible { outline-offset: -2px; }
      `}</style>
    </div>
  );
}
